import { useEffect, useState } from "react";
import api from "../../api/client";

const MODULOS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "productos", label: "Productos" },
  { key: "categorias", label: "Categorías" },
  { key: "pedidos", label: "Pedidos" },
  { key: "configuracion", label: "Configuración" },
  { key: "usuarios", label: "Usuarios" },
  { key: "roles", label: "Roles" },
  { key: "suscripcion", label: "Suscripción" },
];

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

const DEFAULT_PERMISOS = Object.fromEntries(MODULOS.map((m) => [m.key, false]));

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ nombre: "", permisos: { ...DEFAULT_PERMISOS } });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const cargar = () => {
    setLoading(true);
    api.get("/roles/").then((r) => setRoles(r.data)).finally(() => setLoading(false));
  };

  useEffect(cargar, []);

  const openNew = () => {
    setForm({ nombre: "", permisos: { ...DEFAULT_PERMISOS } });
    setEditId(null); setError(""); setModal(true);
  };

  const openEdit = (r) => {
    setForm({ nombre: r.nombre, permisos: { ...DEFAULT_PERMISOS, ...r.permisos } });
    setEditId(r.id); setError(""); setModal(true);
  };

  const togglePermiso = (key) => {
    setForm((prev) => ({ ...prev, permisos: { ...prev.permisos, [key]: !prev.permisos[key] } }));
  };

  const selTodos = () => setForm((prev) => ({ ...prev, permisos: Object.fromEntries(MODULOS.map((m) => [m.key, true])) }));
  const deselTodos = () => setForm((prev) => ({ ...prev, permisos: { ...DEFAULT_PERMISOS } }));

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editId) {
        await api.put(`/roles/${editId}`, form);
      } else {
        await api.post("/roles/", form);
      }
      setModal(false);
      cargar();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este rol?")) return;
    try {
      await api.delete(`/roles/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.detail || "No se puede eliminar");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Roles</h2>
          <p className="text-gray-500 text-sm mt-1">Gestiona permisos por módulo</p>
        </div>
        <button onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          + Nuevo rol
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : roles.length === 0 ? (
          <div className="col-span-3 text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">🔐</div>
            <p className="font-medium">No hay roles aún</p>
          </div>
        ) : (
          roles.map((r) => {
            const activos = Object.values(r.permisos || {}).filter(Boolean).length;
            return (
              <div key={r.id} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-800">{r.nombre}</h3>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(r)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Editar</button>
                    <button onClick={() => eliminar(r.id)} className="text-xs text-red-500 hover:text-red-700 font-medium">Eliminar</button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">{activos} de {MODULOS.length} módulos habilitados</p>
                <div className="flex flex-wrap gap-1">
                  {MODULOS.map((m) => (
                    <span key={m.key} className={`text-xs px-2 py-0.5 rounded-full ${r.permisos?.[m.key] ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Modal open={modal} title={editId ? "Editar rol" : "Nuevo rol"} onClose={() => setModal(false)}>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{error}</div>}
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del rol *</label>
            <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Ej: Vendedor, Supervisor" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Permisos de módulos</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selTodos} className="text-indigo-600 hover:underline">Todos</button>
                <button type="button" onClick={deselTodos} className="text-gray-500 hover:underline">Ninguno</button>
              </div>
            </div>
            <div className="space-y-2">
              {MODULOS.map((m) => (
                <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.permisos[m.key]} onChange={() => togglePermiso(m.key)}
                    className="rounded border-gray-300 text-indigo-600" />
                  <span className="text-sm text-gray-700">{m.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm hover:bg-indigo-700">
              {editId ? "Guardar cambios" : "Crear rol"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
